import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Stack,
  TextField,
  Grid,
  Alert,
  Snackbar,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SchoolIcon from '@mui/icons-material/School';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import CountUp from 'react-countup';
import { useSpring, animated } from 'react-spring';
import api from '../../config/api';

const stats = [
  { label: 'Clients Managed', value: '10+' },
  { label: 'Avg. Open Rate', value: '45%' },
  { label: 'Shopify Sales Growth', value: 'Up to 30%' },
  { label: 'Deliverability', value: '99%+' }
];

const serviceCategories = [
  {
    title: 'Setup & Integration',
    icon: <SettingsIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'Klaviyo Account Setup',
      'Shopify Integration',
      'Custom API Integration',
      'Data Migration'
    ]
  },
  {
    title: 'Automation & Flows',
    icon: <IntegrationInstructionsIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'Abandoned Cart Flows',
      'Post-Purchase Sequences',
      'Welcome Series',
      'Winback Campaigns'
    ]
  },
  {
    title: 'Campaign Management',
    icon: <AutoGraphIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'Email Campaign Strategy',
      'A/B Testing',
      'List Segmentation',
      'Personalization'
    ]
  },
  {
    title: 'Analytics & Reporting',
    icon: <AnalyticsIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'Performance Tracking',
      'ROI Analysis',
      'Deliverability Monitoring',
      'Custom Reports'
    ]
  },
  {
    title: 'Email Deliverability',
    icon: <MarkEmailReadIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'Inbox Placement Optimization',
      'Spam Score Reduction',
      'Domain Authentication',
      'List Hygiene & Maintenance'
    ]
  },
  {
    title: 'Training & Support',
    icon: <SchoolIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'Team Training',
      'Best Practices',
      'Strategy Sessions',
      'Ongoing Support'
    ]
  }
];

const expertisePoints = [
  {
    title: 'Strategic Planning',
    description: 'Custom Klaviyo strategies aligned with your business goals and target audience.'
  },
  {
    title: 'Technical Expertise',
    description: "Deep knowledge of Klaviyo's platform, APIs, and integration capabilities."
  },
  {
    title: 'Data-Driven Approach',
    description: 'Leverage analytics and insights to optimize campaigns and maximize ROI.'
  },
  {
    title: 'Automation Mastery',
    description: 'Create sophisticated automated flows that nurture leads and drive sales.'
  },
  {
    title: 'Deliverability Focus',
    description: 'Ensure your emails reach the inbox with our proven deliverability practices.'
  }
];

const performanceData = [
  { month: 'Jan', openRate: 35, clickRate: 15, revenue: 25000 },
  { month: 'Feb', openRate: 38, clickRate: 17, revenue: 28000 },
  { month: 'Mar', openRate: 42, clickRate: 19, revenue: 32000 },
  { month: 'Apr', openRate: 45, clickRate: 22, revenue: 38000 },
  { month: 'May', openRate: 48, clickRate: 25, revenue: 42000 },
  { month: 'Jun', openRate: 52, clickRate: 28, revenue: 48000 },
];

const statsData = [
  { label: 'Email Open Rate', value: '45%', change: '+12%' },
  { label: 'Click-Through Rate', value: '22%', change: '+8%' },
  { label: 'Revenue Growth', value: '32%', change: '+15%' },
  { label: 'Customer Retention', value: '78%', change: '+10%' },
];

// Add this new constant for hero metrics
const heroMetrics = [
  { label: 'Email Open Rate', value: 45, suffix: '%', color: '#0B60B1' },
  { label: 'Revenue Growth', value: 32, suffix: '%', color: '#4CAF50' },
  { label: 'Customer Retention', value: 78, suffix: '%', color: '#FF9800' }
];

// Add this new constant for animated data
const animatedData = [
  { month: 'Jan', email: 30, sales: 20000, revenue: 15000 },
  { month: 'Feb', email: 35, sales: 25000, revenue: 18000 },
  { month: 'Mar', email: 40, sales: 30000, revenue: 22000 },
  { month: 'Apr', email: 45, sales: 35000, revenue: 28000 },
  { month: 'May', email: 50, sales: 40000, revenue: 32000 },
  { month: 'Jun', email: 55, sales: 45000, revenue: 38000 }
];

export default function KlaviyoPage() {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare form data
      const params = {
        form_name: 'klaviyo_consultation',
        form_source: 'klaviyo_page',
        form_data: JSON.stringify(formData)
      };

      // Send form data to API using our configured axios instance
      const response = await api.get('forms/submit', { params });
      const { data } = response;

      if (data.success) {
        setSnackbar({
          open: true,
          message: data.message || 'Form submitted successfully!',
          severity: 'success'
        });
        // Clear form
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: '',
        });
      } else {
        throw new Error(data.message || 'Form submission failed');
      }
    } catch (error: any) {
      console.error('Form submission error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'An error occurred. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const fadeIn = useSpring({
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
    config: { duration: 1000 }
  });

  return (
    <Box sx={{ bgcolor: '#f7fafd', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box sx={{ bgcolor: 'primary.main', color: '#fff', py: { xs: 6, md: 10 }, position: 'relative' }}>
        <Container maxWidth="lg">
          <animated.div style={fadeIn}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="h3" fontWeight={700} gutterBottom>
                Klaviyo Consulting
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, fontWeight: 400 }}>
                Unlock the full potential of Klaviyo for your ecommerce business
              </Typography>
            </Box>

            {/* Animated Metrics */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 6 }}>
              {heroMetrics.map((metric, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 2,
                    minWidth: 200,
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" fontWeight={700} sx={{ color: metric.color }}>
                    <CountUp end={metric.value} suffix={metric.suffix} duration={2.5} />
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    {metric.label}
                  </Typography>
                </Paper>
              ))}
            </Box>

            {/* Animated Chart */}
            <Box sx={{ height: 300, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 2, p: 3 }}>
              <ResponsiveContainer>
                <LineChart data={animatedData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                  <XAxis dataKey="month" stroke="#fff" />
                  <YAxis yAxisId="left" stroke="#fff" />
                  <YAxis yAxisId="right" orientation="right" stroke="#fff" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      border: 'none',
                      borderRadius: 4
                    }}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="email"
                    stroke="#0B60B1"
                    name="Email Engagement"
                    strokeWidth={2}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="sales"
                    stroke="#4CAF50"
                    name="Sales"
                    strokeWidth={2}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="revenue"
                    stroke="#FF9800"
                    name="Revenue"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </animated.div>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={3} justifyContent="center">
          {stats.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: 'white' }}>
                <Typography variant="h4" color="primary" fontWeight={700}>
                  {stat.value}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {stat.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Why Choose Our Expertise Section - Two Column Layout */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>
          Why Choose Our Klaviyo Expertise
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" textAlign="center" sx={{ mb: 6 }}>
          10+ clients, high open rates, increased Shopify sales, and deliverability excellence
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
          {/* Left Column - Knowledge Points */}
          <Box sx={{ flex: 1 }}>
            <Stack spacing={3}>
              {expertisePoints.map((point, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 3,
                    bgcolor: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      transform: 'translateX(8px)',
                      transition: 'transform 0.2s ease'
                    }
                  }}
                >
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {point.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {point.description}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          </Box>
          {/* Right Column - Charts and Stats */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Performance Chart */}
              <Paper elevation={0} sx={{ p: 3, bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Performance Metrics
                </Typography>
                <Box sx={{ height: 300, width: '100%' }}>
                  <ResponsiveContainer>
                    <LineChart data={performanceData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="openRate"
                        stroke="#0B60B1"
                        name="Open Rate (%)"
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="clickRate"
                        stroke="#094a8f"
                        name="Click Rate (%)"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="revenue"
                        stroke="#4CAF50"
                        name="Revenue ($)"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>

              {/* Stats Grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                {statsData.map((stat, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: 'white',
                      borderRadius: 2,
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="h5" color="primary" fontWeight={700}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: stat.change.startsWith('+') ? 'success.main' : 'error.main',
                        fontWeight: 600
                      }}
                    >
                      {stat.change}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Services Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>
          Our Services
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" textAlign="center" sx={{ mb: 6 }}>
          Comprehensive Klaviyo consulting services to boost your ecommerce performance
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
          {serviceCategories.map((category, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 3,
                bgcolor: 'white',
                borderRadius: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                {category.icon}
                <Typography variant="h6" fontWeight={600}>
                  {category.title}
                </Typography>
              </Box>
              <Stack spacing={1.5}>
                {category.services.map((service, serviceIndex) => (
                  <Box key={serviceIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckCircleIcon color="primary" sx={{ fontSize: 20 }} />
                    <Typography variant="body1">{service}</Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          ))}
        </Box>
      </Container>

      {/* Consultation Form Section - Two Column Layout */}
      <Box sx={{ bgcolor: '#f7fafd', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
            {/* Left Column - Knowledge Points */}
            <Box sx={{ flex: 1, pr: { md: 4 } }}>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                Ready to Transform Your Ecommerce?
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" paragraph>
                Our team of Klaviyo experts is ready to help you:
              </Typography>
              <Stack spacing={2}>
                {[
                  'Set up and optimize your Klaviyo account',
                  'Create high-converting email flows',
                  'Integrate with your Shopify store',
                  'Improve email deliverability',
                  'Analyze and optimize campaign performance',
                  'Train your team on best practices'
                ].map((point, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CheckCircleIcon color="primary" sx={{ mt: 0.5 }} />
                    <Typography variant="body1">{point}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
            {/* Right Column - Form */}
            <Box sx={{ flex: 1 }}>
              <Paper elevation={0} sx={{ p: 4, borderRadius: 4, bgcolor: 'white', height: '100%' }}>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  Request a Free Klaviyo Consultation
                </Typography>
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    fullWidth
                  />
                  <TextField
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    fullWidth
                  />
                  <TextField
                    label="Company / Store"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    fullWidth
                  />
                  <TextField
                    label="Phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    fullWidth
                  />
                  <TextField
                    label="Message"
                    name="message"
                    multiline
                    rows={4}
                    value={formData.message}
                    onChange={handleInputChange}
                    fullWidth
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={loading}
                    sx={{ fontWeight: 600, mt: 2 }}
                  >
                    {loading ? 'Submitting...' : 'Schedule a Consultation'}
                  </Button>
                </Box>
              </Paper>
            </Box>
          </Box>
        </Container>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
} 